/**
 * BookingsPage — Phase 6.
 *
 * Replaces the mock-only Bookings.tsx with real API calls via
 * ``useBookings`` / lifecycle mutation hooks.
 *
 * Structure:
 *  - Top tabs: All | Trials | Make-ups | Ad-hoc
 *  - Inline filters: status dropdown, date-from, date-to
 *  - Booking table with row actions (confirm, cancel, mark no-show)
 *  - "New Booking" modal (trial / makeup / regular / ad-hoc flows)
 *  - NoEntitlementBanner: surfaces 422 no_eligible_entitlement inline
 *    with a "Continue anyway" force-override path.
 *
 * All interactive surfaces carry ``data-testid`` per ADR 0003.
 */

import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { fromZonedTime } from 'date-fns-tz';

import { Button, Card, Dropdown, Modal, Tabs, TabPanel } from '@/components/common';
import { PageContainer } from '@/components/layout';
import { NoEntitlementBanner } from '@/components/bookings/NoEntitlementBanner';
import { ApiError } from '@core/api/client';
import { useAuth } from '@/context';
import {
  useBookings,
  useCreateBooking,
  useCancelBooking,
  useConfirmBooking,
  useMarkNoShow,
  type BookingCreate,
  type BookingResponse,
  type BookingsFilters,
} from '@features/bookings/api/bookings';
import {
  BOOKING_STATUS_OPTIONS,
  BOOKING_TYPE_OPTIONS,
  BOOKING_TABS,
  statusColor,
  statusLabel,
  typeLabel,
  type BookingTabId,
} from '@features/bookings/config/ui';


/**
 * Convert a wall-clock string from a ``<input type="datetime-local">`` to a
 * UTC ISO string interpreted in the tenant timezone.
 *
 * Without this, ``new Date("2026-05-28T10:00")`` is interpreted in the
 * browser's local timezone — a tenant operator in Asia/Kolkata using a
 * laptop in UTC would see their bookings drift by 5h30. ``fromZonedTime``
 * gives us the deterministic conversion the backend expects.
 */
function wallClockToUtcIso(wallClock: string, tenantTz: string): string {
  if (!wallClock) return '';
  return fromZonedTime(wallClock, tenantTz).toISOString();
}

/**
 * Convert a ``YYYY-MM-DD`` filter date to a UTC ISO string anchored at
 * midnight in the tenant timezone. ``to`` is anchored at end-of-day so the
 * filter is inclusive of the selected day.
 */
function dateFilterToUtcIso(
  date: string,
  tenantTz: string,
  position: 'start' | 'end',
): string {
  if (!date) return '';
  const time = position === 'start' ? 'T00:00:00' : 'T23:59:59.999';
  return fromZonedTime(`${date}${time}`, tenantTz).toISOString();
}

// ---------------------------------------------------------------------------
// Styled helpers
// ---------------------------------------------------------------------------

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const StatusDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const StatusCell = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
`;

const ForceTag = styled.span`
  font-size: 10px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  color: #92400e;
  border-radius: 4px;
  padding: 1px 5px;
  font-weight: 600;
`;

const LateCancelTag = styled.span`
  font-size: 10px;
  background: #fee2e2;
  border: 1px solid #ef4444;
  color: #991b1b;
  border-radius: 4px;
  padding: 1px 5px;
  font-weight: 600;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing['2xl']};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing['2xl']};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ErrorState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing['2xl']};
  color: ${({ theme }) => theme.colors.textSecondary ?? '#dc2626'};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
`;

const Th = styled.th`
  text-align: left;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  vertical-align: middle;
`;

const SubText = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;

// ---------------------------------------------------------------------------
// New Booking modal form
// ---------------------------------------------------------------------------

interface NewBookingFormState {
  bookingType: BookingCreate['booking_type'];
  subjectId: string;
  teacherId: string;
  roomId: string;
  studentId: string;
  sessionId: string;
  startsAt: string;
  endsAt: string;
  notes: string;
  // Trial-only
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  studentName: string;
  studentAge: string;
}

const INITIAL_FORM: NewBookingFormState = {
  bookingType: 'trial',
  subjectId: '',
  teacherId: '',
  roomId: '',
  studentId: '',
  sessionId: '',
  startsAt: '',
  endsAt: '',
  notes: '',
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  studentName: '',
  studentAge: '',
};

interface NewBookingModalProps extends BookingPickerProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Cross-feature picker injection (per the render-prop composition pattern —
 * see docs/03-architecture/entity-framework.md "Cross-feature composition").
 * Bookings cannot import @features/people or @features/catalog directly
 * (import-boundary lint rule), so the subject/student/teacher pickers and
 * the entitlement hint are supplied by the composition root
 * (src/routes/index.tsx). Sensible plain-input fallbacks are used when a
 * picker isn't supplied, so this feature still renders standalone in tests.
 */
export interface BookingPickerProps {
  renderSubjectPicker?: (value: string, onChange: (v: string) => void) => React.ReactNode;
  renderStudentPicker?: (
    value: string,
    onChange: (v: string) => void,
    search: string,
    onSearchChange: (v: string) => void,
  ) => React.ReactNode;
  renderTeacherPicker?: (value: string, onChange: (v: string) => void) => React.ReactNode;
  renderEntitlementHint?: (studentId: string, subjectId: string) => React.ReactNode;
}


const FormGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.md};
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FormLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const FormInput = styled.input`
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FormSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const SectionTitle = styled.h4`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: ${({ theme }) => theme.spacing.sm} 0 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const NewBookingModal: React.FC<NewBookingModalProps> = ({
  isOpen,
  onClose,
  renderSubjectPicker,
  renderStudentPicker,
  renderTeacherPicker,
  renderEntitlementHint,
}) => {
  const [form, setForm] = useState<NewBookingFormState>(INITIAL_FORM);
  const [studentSearch, setStudentSearch] = useState('');
  const [noEntitlement, setNoEntitlement] = useState(false);
  const createMut = useCreateBooking();
  const { user } = useAuth();
  const tenantTz = user?.tenant_timezone ?? 'UTC';

  const update = useCallback(
    <K extends keyof NewBookingFormState>(key: K, value: NewBookingFormState[K]) => {
      setForm((f) => ({ ...f, [key]: value }));
      // Clear the no-entitlement banner when the operator changes key fields.
      if (['studentId', 'subjectId', 'bookingType'].includes(key)) {
        setNoEntitlement(false);
      }
    },
    [],
  );



  const buildPayload = useCallback(
    (force = false): BookingCreate => {
      const consuming = form.bookingType !== 'trial';
      // ``datetime-local`` inputs return a naive wall-clock string. Convert
      // through the tenant timezone so the backend receives a true UTC
      // timestamp (matches the UTC-everywhere architecture invariant).
      const startsAtUtc = wallClockToUtcIso(form.startsAt, tenantTz);
      const endsAtUtc = wallClockToUtcIso(form.endsAt, tenantTz);
      const base: BookingCreate = {
        booking_type: form.bookingType,
        source: 'admin',
        subject_id: form.subjectId,
        starts_at: startsAtUtc,
        ends_at: endsAtUtc,
        force,
        ...(form.teacherId ? { teacher_id: form.teacherId } : {}),
        ...(form.roomId ? { room_id: form.roomId } : {}),
        ...(form.sessionId ? { session_id: form.sessionId } : {}),
        ...(form.notes ? { notes: form.notes } : {}),
      };

      if (consuming && form.studentId) {
        (base as BookingCreate).student_id = form.studentId;
      }

      if (form.bookingType === 'trial') {
        (base as BookingCreate).external_contact = {
          name: form.contactName,
          phone: form.contactPhone,
          ...(form.contactEmail ? { email: form.contactEmail } : {}),
          ...(form.studentName ? { student_name: form.studentName } : {}),
          ...(form.studentAge ? { student_age: parseInt(form.studentAge, 10) } : {}),
        };
        if (form.studentId) {
          (base as BookingCreate).student_id = form.studentId;
        }
      }

      return base;
    },
    [form, tenantTz],
  );

  const submit = useCallback(
    async (force = false) => {
      const payload = buildPayload(force);
      try {
        await createMut.mutateAsync(payload);
        toast.success('Booking created');
        setForm(INITIAL_FORM);
        setNoEntitlement(false);
        onClose();
      } catch (err) {
        if (err instanceof ApiError && err.code === 'no_eligible_entitlement' && !force) {
          setNoEntitlement(true);
          return;
        }
        toast.error(err instanceof Error ? err.message : 'Failed to create booking');
      }
    },
    [buildPayload, createMut, onClose],
  );

  const isTrial = form.bookingType === 'trial';
  const loading = createMut.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { setForm(INITIAL_FORM); setNoEntitlement(false); onClose(); }}
      title="New Booking"
      size="lg"
      data-testid="candeza-bookings-create-modal"
    >
      <FormGrid>
        {/* Booking type */}
        <FormField>
          <FormLabel htmlFor="bk-type">Booking type</FormLabel>
          <FormSelect
            id="bk-type"
            data-testid="candeza-bookings-create-type-select"
            value={form.bookingType}
            onChange={(e) => update('bookingType', e.target.value as BookingCreate['booking_type'])}
          >
            {BOOKING_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </FormSelect>
        </FormField>

        {/* Subject */}
        <FormField>
          <FormLabel htmlFor="bk-subject">Subject</FormLabel>
          {renderSubjectPicker ? (
            renderSubjectPicker(form.subjectId, (v) => update('subjectId', v))
          ) : (
            <FormInput
              id="bk-subject"
              data-testid="candeza-bookings-create-subject-input"
              placeholder="e.g. subj-guitar"
              value={form.subjectId}
              onChange={(e) => update('subjectId', e.target.value)}
            />
          )}
        </FormField>

        {/* Student (non-trial requires it; trial may also link) */}
        <FormField>
          <FormLabel htmlFor="bk-student">
            Student {isTrial ? '(optional for trial)' : '*'}
          </FormLabel>
          {renderStudentPicker ? (
            renderStudentPicker(form.studentId, (v) => update('studentId', v), studentSearch, setStudentSearch)
          ) : (
            <FormInput
              id="bk-student"
              data-testid="candeza-bookings-create-student-input"
              placeholder="Student ID"
              value={form.studentId}
              onChange={(e) => update('studentId', e.target.value)}
            />
          )}
          {form.studentId && renderEntitlementHint?.(form.studentId, form.subjectId)}
        </FormField>

        {/* Session (optional) */}
        <FormField>
          <FormLabel htmlFor="bk-session">Session ID (optional)</FormLabel>
          <FormInput
            id="bk-session"
            data-testid="candeza-bookings-create-session-input"
            placeholder="Links to an existing session"
            value={form.sessionId}
            onChange={(e) => update('sessionId', e.target.value)}
          />
        </FormField>

        {/* Teacher + Room */}
        <FormRow>
          <FormField>
            <FormLabel htmlFor="bk-teacher">Teacher</FormLabel>
            {renderTeacherPicker ? (
              renderTeacherPicker(form.teacherId, (v) => update('teacherId', v))
            ) : (
              <FormInput
                id="bk-teacher"
                data-testid="candeza-bookings-create-teacher-input"
                placeholder="Teacher ID"
                value={form.teacherId}
                onChange={(e) => update('teacherId', e.target.value)}
              />
            )}
          </FormField>
          <FormField>
            <FormLabel htmlFor="bk-room">Location / Room</FormLabel>
            <FormInput
              id="bk-room"
              data-testid="candeza-bookings-create-room-input"
              placeholder="Room / location name"
              value={form.roomId}
              onChange={(e) => update('roomId', e.target.value)}
            />
          </FormField>
        </FormRow>



        {/* Date + time */}
        <FormRow>
          <FormField>
            <FormLabel htmlFor="bk-starts">Starts at *</FormLabel>
            <FormInput
              id="bk-starts"
              type="datetime-local"
              data-testid="candeza-bookings-create-starts-input"
              value={form.startsAt}
              onChange={(e) => update('startsAt', e.target.value)}
            />
          </FormField>
          <FormField>
            <FormLabel htmlFor="bk-ends">Ends at *</FormLabel>
            <FormInput
              id="bk-ends"
              type="datetime-local"
              data-testid="candeza-bookings-create-ends-input"
              value={form.endsAt}
              onChange={(e) => update('endsAt', e.target.value)}
            />
          </FormField>
        </FormRow>

        {/* Trial-specific fields */}
        {isTrial && (
          <>
            <SectionTitle>Trial contact</SectionTitle>
            <FormRow>
              <FormField>
                <FormLabel htmlFor="bk-contact-name">Contact name *</FormLabel>
                <FormInput
                  id="bk-contact-name"
                  data-testid="candeza-bookings-create-contact-name-input"
                  placeholder="Parent / guardian name"
                  value={form.contactName}
                  onChange={(e) => update('contactName', e.target.value)}
                />
              </FormField>
              <FormField>
                <FormLabel htmlFor="bk-contact-phone">Phone *</FormLabel>
                <FormInput
                  id="bk-contact-phone"
                  data-testid="candeza-bookings-create-contact-phone-input"
                  placeholder="+91 …"
                  value={form.contactPhone}
                  onChange={(e) => update('contactPhone', e.target.value)}
                />
              </FormField>
            </FormRow>
            <FormRow>
              <FormField>
                <FormLabel htmlFor="bk-contact-email">Email</FormLabel>
                <FormInput
                  id="bk-contact-email"
                  type="email"
                  data-testid="candeza-bookings-create-contact-email-input"
                  placeholder="optional"
                  value={form.contactEmail}
                  onChange={(e) => update('contactEmail', e.target.value)}
                />
              </FormField>
              <FormField>
                <FormLabel htmlFor="bk-student-name">Student name</FormLabel>
                <FormInput
                  id="bk-student-name"
                  data-testid="candeza-bookings-create-student-name-input"
                  placeholder="Child's name"
                  value={form.studentName}
                  onChange={(e) => update('studentName', e.target.value)}
                />
              </FormField>
            </FormRow>
            <FormField>
              <FormLabel htmlFor="bk-student-age">Student age</FormLabel>
              <FormInput
                id="bk-student-age"
                type="number"
                min={2}
                max={120}
                data-testid="candeza-bookings-create-student-age-input"
                placeholder="e.g. 8"
                value={form.studentAge}
                onChange={(e) => update('studentAge', e.target.value)}
              />
            </FormField>
          </>
        )}

        {/* Notes */}
        <FormField>
          <FormLabel htmlFor="bk-notes">Notes</FormLabel>
          <FormInput
            as="textarea"
            id="bk-notes"
            data-testid="candeza-bookings-create-notes-input"
            placeholder="Internal notes (optional)"
            value={form.notes}
            onChange={(e) => update('notes', (e.target as HTMLTextAreaElement).value)}
            style={{ minHeight: '60px', resize: 'vertical' }}
          />
        </FormField>

        {/* No-entitlement inline banner */}
        {noEntitlement && (
          <NoEntitlementBanner
            onForceOverride={() => submit(true)}
            onDismiss={() => setNoEntitlement(false)}
            loading={loading}
          />
        )}
      </FormGrid>

      <ModalFooter>
        <Button
          variant="ghost"
          onClick={() => { setForm(INITIAL_FORM); setNoEntitlement(false); onClose(); }}
          disabled={loading}
          data-testid="candeza-bookings-create-cancel-btn"
        >
          Cancel
        </Button>
        {!noEntitlement && (
          <Button
            variant="primary"
            onClick={() => submit(false)}
            isLoading={loading}
            disabled={loading}
            data-testid="candeza-bookings-create-submit-btn"
          >
            Create booking
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Cancel modal
// ---------------------------------------------------------------------------

interface CancelModalProps {
  booking: BookingResponse;
  onClose: () => void;
}

const CancelModal: React.FC<CancelModalProps> = ({ booking, onClose }) => {
  const [reason, setReason] = useState('');
  const cancelMut = useCancelBooking();

  const submit = async () => {
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    try {
      await cancelMut.mutateAsync({ id: booking.id, body: { reason } });
      toast.success('Booking cancelled');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to cancel booking');
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Cancel booking"
      size="sm"
      data-testid="candeza-bookings-cancel-modal"
    >
      <FormGrid>
        <FormField>
          <FormLabel htmlFor="cancel-reason">Reason *</FormLabel>
          <FormInput
            as="textarea"
            id="cancel-reason"
            data-testid="candeza-bookings-cancel-reason-input"
            placeholder="Why is this booking being cancelled?"
            value={reason}
            onChange={(e) => setReason((e.target as HTMLTextAreaElement).value)}
            style={{ minHeight: '80px', resize: 'vertical' }}
          />
        </FormField>
      </FormGrid>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose} disabled={cancelMut.isPending}
          data-testid="candeza-bookings-cancel-dismiss-btn">
          Keep booking
        </Button>
        <Button variant="danger" onClick={submit} isLoading={cancelMut.isPending}
          data-testid="candeza-bookings-cancel-confirm-btn">
          Cancel booking
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Booking row actions
// ---------------------------------------------------------------------------

interface RowActionsProps {
  booking: BookingResponse;
  onCancel: (b: BookingResponse) => void;
}

const RowActions: React.FC<RowActionsProps> = ({ booking, onCancel }) => {
  const confirmMut = useConfirmBooking();
  const noShowMut = useMarkNoShow();

  const handleConfirm = async () => {
    try {
      await confirmMut.mutateAsync(booking.id);
      toast.success('Booking confirmed');
    } catch (err) { toast.error(err instanceof Error ? err.message : 'Error'); }
  };

  const handleNoShow = async () => {
    try {
      await noShowMut.mutateAsync(booking.id);
      toast.success('Marked as no-show');
    } catch (err) { toast.error(err instanceof Error ? err.message : 'Error'); }
  };

  const isCancellable = !['cancelled', 'completed', 'no_show'].includes(booking.status);

  return (
    <ActionRow>
      {booking.status === 'pending' && (
        <Button variant="outline" size="sm"
          onClick={handleConfirm}
          isLoading={confirmMut.isPending}
          data-testid={`candeza-bookings-row-confirm-btn-${booking.id}`}>
          Confirm
        </Button>
      )}
      {booking.status === 'confirmed' && (
        <Button variant="outline" size="sm"
          onClick={handleNoShow}
          isLoading={noShowMut.isPending}
          data-testid={`candeza-bookings-row-noshow-btn-${booking.id}`}>
          No-show
        </Button>
      )}
      {isCancellable && (
        <Button variant="danger" size="sm"
          onClick={() => onCancel(booking)}
          data-testid={`candeza-bookings-row-cancel-btn-${booking.id}`}>
          Cancel
        </Button>
      )}
    </ActionRow>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const BookingsPage: React.FC<BookingPickerProps> = ({
  renderSubjectPicker,
  renderStudentPicker,
  renderTeacherPicker,
  renderEntitlementHint,
}) => {
  const [tab, setTab] = useState<BookingTabId>('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<BookingResponse | null>(null);
  const { user } = useAuth();
  const tenantTz = user?.tenant_timezone ?? 'UTC';

  const filters: BookingsFilters = useMemo(() => ({
    ...(tab !== 'all' ? { bookingType: tab as BookingsFilters['bookingType'] } : {}),
    ...(statusFilter ? { status: statusFilter as BookingsFilters['status'] } : {}),
    // Date filters use ``YYYY-MM-DD`` inputs which JS would otherwise parse
    // as UTC midnight — wrong for any tenant outside UTC. Convert in the
    // tenant timezone so "today" means the operator's today.
    ...(dateFrom ? { from: dateFilterToUtcIso(dateFrom, tenantTz, 'start') } : {}),
    ...(dateTo ? { to: dateFilterToUtcIso(dateTo, tenantTz, 'end') } : {}),
    limit: 100,
  }), [tab, statusFilter, dateFrom, dateTo, tenantTz]);

  const { data, isLoading, isError } = useBookings(filters);

  const tabs = BOOKING_TABS.map((t) => ({ id: t.id, label: t.label }));

  const statusOptions = [
    { value: '', label: 'All statuses' },
    ...BOOKING_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
  ];

  return (
    <PageContainer
      title="Bookings"
      subtitle="Trial, make-up, ad-hoc and regular session bookings"
      data-testid="candeza-bookings-page"
      actions={
        <Button
          variant="primary"
          onClick={() => setNewModalOpen(true)}
          data-testid="candeza-bookings-new-btn"
        >
          + New booking
        </Button>
      }
    >
      <Card padding="lg">
        {/* Tabs */}
        <div data-testid="candeza-bookings-tabs">
          <Tabs tabs={tabs} activeTab={tab} onChange={(t) => setTab(t as BookingTabId)}>

            {/* Filters row (same for all tabs) */}
            <FiltersRow>
              <div>
                <Dropdown
                  options={statusOptions}
                  value={statusFilter}
                  onChange={setStatusFilter}
                  testId="candeza-bookings-status-filter"
                />
              </div>
              <FormInput
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="candeza-bookings-date-from-input"
                placeholder="From"
                style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
              />
              <FormInput
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="candeza-bookings-date-to-input"
                placeholder="To"
                style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}
              />
            </FiltersRow>

            {BOOKING_TABS.map((tabDef) => (
              <TabPanel key={tabDef.id} value={tabDef.id} activeValue={tab}>
                <BookingsTable
                  items={data?.items ?? []}
                  isLoading={isLoading}
                  isError={isError}
                  hasMore={data?.has_more ?? false}
                  onCancel={setCancelTarget}
                />
              </TabPanel>
            ))}
          </Tabs>
        </div>
      </Card>

      <NewBookingModal
        isOpen={newModalOpen}
        onClose={() => setNewModalOpen(false)}
        renderSubjectPicker={renderSubjectPicker}
        renderStudentPicker={renderStudentPicker}
        renderTeacherPicker={renderTeacherPicker}
        renderEntitlementHint={renderEntitlementHint}
      />

      {cancelTarget && (
        <CancelModal
          booking={cancelTarget}
          onClose={() => setCancelTarget(null)}
        />
      )}
    </PageContainer>
  );
};

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

interface BookingsTableProps {
  items: BookingResponse[];
  isLoading: boolean;
  isError: boolean;
  hasMore: boolean;
  onCancel: (b: BookingResponse) => void;
}

function formatDt(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

const TruncatedBanner = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: #fffbeb;
  border: 1px solid #f59e0b;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: #92400e;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
`;

const BookingsTable: React.FC<BookingsTableProps> = ({
  items, isLoading, isError, hasMore, onCancel,
}) => {
  if (isLoading) {
    return <LoadingState data-testid="candeza-bookings-loading">Loading bookings…</LoadingState>;
  }
  if (isError) {
    return <ErrorState data-testid="candeza-bookings-error">Failed to load bookings.</ErrorState>;
  }
  if (!items.length) {
    return <EmptyState data-testid="candeza-bookings-empty">No bookings found.</EmptyState>;
  }

  return (
    <>
      <Table data-testid="candeza-bookings-table">
      <thead>
        <tr>
          <Th>Type</Th>
          <Th>Student / Contact</Th>
          <Th>Subject</Th>
          <Th>Starts at</Th>
          <Th>Status</Th>
          <Th>Actions</Th>
        </tr>
      </thead>
      <tbody>
        {items.map((bk) => (
          <tr
            key={bk.id}
            data-testid={`candeza-bookings-row-${bk.id}`}
            data-status={bk.status}
            data-type={bk.booking_type}
          >
            <Td>
              {typeLabel(bk.booking_type)}
              {bk.force_override && <><br /><ForceTag>force</ForceTag></>}
            </Td>
            <Td>
              {bk.student_id ?? bk.external_contact?.name ?? '—'}
              {bk.external_contact?.phone && (
                <SubText>{bk.external_contact.phone}</SubText>
              )}
            </Td>
            <Td>{bk.subject_id}</Td>
            <Td>
              {formatDt(bk.starts_at)}
              {bk.late_cancellation && (
                <><br /><LateCancelTag>late cancel</LateCancelTag></>
              )}
            </Td>
            <Td>
              <StatusCell>
                <StatusDot $color={statusColor(bk.status)} />
                {statusLabel(bk.status)}
              </StatusCell>
            </Td>
            <Td>
              <RowActions booking={bk} onCancel={onCancel} />
            </Td>
          </tr>
        ))}
      </tbody>
    </Table>
      {hasMore && (
        <TruncatedBanner data-testid="candeza-bookings-truncated-banner">
          Showing the first batch — narrow filters (or pick a tighter date range)
          to see the full picture. Cursor pagination ships in Phase 6.5.
        </TruncatedBanner>
      )}
    </>
  );
};





